/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_bridge.infra

import io.peekandpoke.klang.audio_bridge.IgnitorDsl
import io.peekandpoke.klang.audio_bridge.KatalystDsl
import io.peekandpoke.klang.audio_bridge.MonoSamplePcm
import io.peekandpoke.klang.audio_bridge.RealtimeVoice
import io.peekandpoke.klang.audio_bridge.SampleMetadata
import io.peekandpoke.klang.audio_bridge.SampleRequest
import io.peekandpoke.klang.audio_bridge.ScheduledVoice
import io.peekandpoke.klang.audio_bridge.WireFormat
import io.peekandpoke.klang.audio_bridge.WireName
import io.peekandpoke.klang.common.infra.KlangMessageReceiver
import io.peekandpoke.klang.common.infra.KlangMessageSender
import io.peekandpoke.klang.common.infra.KlangRingBuffer

class KlangCommLink(capacity: Int = 8192) {

    companion object {
        /** Playback ID for system-wide messages (not tied to a specific playback) */
        const val SYSTEM_PLAYBACK_ID = "--SYSTEM--"
    }

    /** Sent from the frontend to the backend */
    @WireFormat
    sealed interface Cmd {
        val playbackId: String

        @WireName("cleanup")
        data class Cleanup(
            override val playbackId: String,
        ) : Cmd

        /**
         * Drop all not-yet-played voices for a playback. NOT on the production hot path — the frontend
         * clears via [ReplaceVoices] (`afterTimeSec = null`); kept as a primitive for tests.
         */
        @WireName("clear-scheduled")
        data class ClearScheduled(
            override val playbackId: String,
        ) : Cmd

        @WireName("replace-voices")
        data class ReplaceVoices(
            override val playbackId: String,
            val voices: List<ScheduledVoice>,
            val afterTimeSec: Double? = null,
        ) : Cmd

        /**
         * Schedule a single voice. NOT on the production hot path — the frontend always batches via
         * [ScheduleVoices]; kept as a primitive for tests/benchmarks. To replace a playback's voices
         * use [ReplaceVoices], not a clear-then-schedule flag.
         */
        @WireName("schedule-voice")
        data class ScheduleVoice(
            override val playbackId: String,
            val voice: ScheduledVoice,
        ) : Cmd

        /**
         * Batch variant of [ScheduleVoice]. All voices in the list share a single nowSec snapshot
         * on the backend, so per-batch jitter (postMessage interleaving with audio blocks) cannot
         * push later voices into the past relative to earlier ones.
         */
        @WireName("schedule-voices")
        data class ScheduleVoices(
            override val playbackId: String,
            val voices: List<ScheduledVoice>,
        ) : Cmd

        /**
         * Starts a realtime voice immediately — see [RealtimeVoice]. Unlike [ScheduleVoice] there
         * is no start time on the wire: the backend stamps "now" at receipt and promotes the voice
         * straight to active, bypassing the scheduled heap and epoch anchoring entirely.
         */
        @WireName("start-realtime-voice")
        data class StartRealtimeVoice(
            override val playbackId: String,
            val voice: RealtimeVoice,
        ) : Cmd

        /**
         * Releases the gate of every realtime voice with [liveId] — the voice enters its ADSR
         * release from the current level (a note-off, NOT a cut). Unknown liveId is a no-op:
         * releasing a key after a fixed-length voice already ended is normal.
         */
        @WireName("stop-realtime-voice")
        data class StopRealtimeVoice(
            override val playbackId: String,
            val liveId: Int,
        ) : Cmd

        /**
         * Moves the playback's output gain to [gain] over [rampSec] seconds, from where it is when the backend
         * receives this (a fade needs no musical time); `rampSec <= 0` sets it at once (KLANG-00406, step 5).
         * Sent while the id's engine still releases a stopped playback, it goes to a fresh engine for the start it comes
         * before, and the release fades on undisturbed.
         *
         * **Sent only while the playback is started**, between its start and its stop; the frontend's playback is
         * the gate. It stores every value and sends it again at a (re)start, BEFORE the first [ScheduleVoices]: the
         * backend creates the engine for it when none is filed under the id (one port keeps the order, so the first
         * block that sounds already has the gain). Sent after the stop's [Cleanup], it would create an empty engine
         * that nothing ever stops: it would render for ever.
         */
        @WireName("set-gain")
        data class SetGain(
            override val playbackId: String,
            val gain: Double,
            val rampSec: Double,
        ) : Cmd

        /** Registers a custom IgnitorDsl in the backend's exciter registry. */
        @WireName("register-ignitor")
        data class RegisterIgnitor(
            override val playbackId: String,
            val name: String,
            val dsl: IgnitorDsl,
        ) : Cmd

        /**
         * Registers a custom KatalystDsl in the backend's chain registry, the one registry both
         * positions resolve against: an orbit's `katalyst` name and the playback's `master` name.
         *
         * Sent once per unique chain, before any voice referencing [name] is scheduled: the same
         * send-once contract as [RegisterIgnitor].
         */
        @WireName("register-katalyst")
        data class RegisterKatalyst(
            override val playbackId: String,
            val name: String,
            val dsl: KatalystDsl,
        ) : Cmd

        sealed interface Sample : Cmd {
            @WireName("sample-not-found")
            data class NotFound(
                override val req: SampleRequest,
            ) : Sample {
                override val playbackId: String = SYSTEM_PLAYBACK_ID
            }

            @WireName("sample-complete")
            data class Complete(
                override val req: SampleRequest,
                val note: String?,
                val pitchHz: Double,
                val sample: MonoSamplePcm,
            ) : Sample {
                override val playbackId: String = SYSTEM_PLAYBACK_ID

                /**
                 * Splits the PCM for upload.
                 *
                 * [chunkFrames] counts **DoubleArray elements** (mono frames), NOT bytes: at 8
                 * bytes each, 64 * 1024 frames is a 512 KB message. The parameter used to be
                 * called `chunkSizeBytes`, which read as 8× smaller than it is (64 KB believed,
                 * 512 KB actual) and made the upload look far more throttled than it was.
                 *
                 * Chunking originally existed to keep kotlinx-serialization decode off the audio
                 * thread in slices. That cost is gone (the KSP wire codec replaced it), and what
                 * remains per chunk is one structured-clone plus the `copyInto` in
                 * `SampleStore.addSample` — linear in [chunkFrames], so this still bounds the
                 * worst-case audio-thread hiccup. It is a SIZE bound, not a rate limit; the rate
                 * is set by how many chunks the host forwards per tick.
                 *
                 * NB: when the PCM length is an exact multiple of [chunkFrames] the last entry is
                 * an empty chunk carrying `isLastChunk`. Harmless (an empty `copyInto` is a no-op)
                 * and it still completes the sample, but it is one wasted message.
                 */
                fun toChunks(chunkFrames: Int = 64 * 1024): List<Chunk> {
                    val numChunks = (sample.pcm.size / chunkFrames) + 1

                    return (0 until numChunks).map { i ->
                        val start = i * chunkFrames
                        val end = minOf(sample.pcm.size, (i + 1) * chunkFrames)

                        Chunk(
                            req = req,
                            note = note,
                            pitchHz = pitchHz,
                            sampleRate = sample.sampleRate,
                            meta = sample.meta,
                            totalSize = sample.pcm.size,
                            isLastChunk = i == numChunks - 1,
                            chunkOffset = start,
                            data = sample.pcm.copyOfRange(start, end),
                        )
                    }
                }
            }

            @Suppress("ArrayInDataClass")
            @WireName("sample-chunk")
            data class Chunk(
                override val req: SampleRequest,
                val note: String?,
                val pitchHz: Double,
                val sampleRate: Int,
                val meta: SampleMetadata,
                val totalSize: Int,
                val isLastChunk: Boolean,
                val chunkOffset: Int,
                val data: DoubleArray,
            ) : Sample {
                override val playbackId: String = SYSTEM_PLAYBACK_ID
            }

            val req: SampleRequest
        }
    }

    /** Sent from the backend to the frontend */
    @WireFormat
    sealed interface Feedback {
        val playbackId: String

        /**
         * Emitted once after the backend has completed its warmup pass: JIT + cache priming, AND the
         * stocking of the resource warehouse — the warmup plays sixteen wet orbits, one per block,
         * and its disposal returns their cylinders, delay rings and reverb networks to the shelves,
         * so the first song's first frame builds nothing. The frontend awaits this before starting
         * the first playback.
         */
        @WireName("backend-ready")
        data class BackendReady(
            override val playbackId: String = SYSTEM_PLAYBACK_ID,
        ) : Feedback

        @WireName("request-sample")
        data class RequestSample(
            override val playbackId: String,
            val req: SampleRequest,
        ) : Feedback

        @WireName("sample-received")
        data class SampleReceived(
            override val playbackId: String,
            val req: SampleRequest,
        ) : Feedback

        /**
         * The backend started a playback (KLANG-00399): its second 0 is [epochSec] on the backend's timeline, the later
         * of the `playbackStartTime` its first voice asked for and the backend's now. Sent once per start, a resume
         * after a stop included; every voice's `startTime` counts from it.
         */
        @WireName("playback-anchored")
        data class PlaybackAnchored(
            override val playbackId: String,
            val epochSec: Double,
        ) : Feedback

        @WireName("diagnostics")
        data class Diagnostics(
            override val playbackId: String,
            /** Backend sample rate in Hz (e.g. 44100, 48000) */
            val sampleRate: Int,
            /**
             * Rendering headroom as a ratio (not time-based).
             * - 1.0 = idle (all time available)
             * - 0.0 = full load (using all available time)
             * - <0.0 = overload (glitching, taking longer than block duration)
             * Calculated as: 1.0 - (renderDuration / blockDuration)
             */
            val renderHeadroom: Double,
            /**
             * Number of voices currently active (rendering audio)
             */
            val activeVoiceCount: Int,
            /**
             * State of all allocated cylinders (mixing channels)
             */
            val cylinders: List<CylinderState>,
            /**
             * The backend's timeline at the end of the block just rendered, in ms: its start plus the frames rendered,
             * never a wall clock (KLANG-00399). A backend that stutters renders, and so reports, fewer frames. The
             * frontend's `PlaybackClock` runs on it.
             */
            val backendNowMs: Double,
            /** AudioContext.baseLatency in ms (browser's audio processing pipeline). 0.0 on JVM. */
            val baseLatencyMs: Double = 0.0,
            /** AudioContext.outputLatency in ms (hardware/device latency, e.g. Bluetooth). 0.0 on JVM. */
            val outputDeviceLatencyMs: Double = 0.0,
            /**
             * Total output latency in ms: `baseLatencyMs + outputDeviceLatencyMs` **plus the master
             * limiter's lookahead**, which happens inside the worklet where the browser's own
             * latency figures cannot see it.
             */
            val outputLatencyMs: Double = 0.0,
            /**
             * The resource warehouse's stats — what the backend holds and what happened to it.
             * Maintained INCREMENTALLY on the backend (each shelf bumps a version when something
             * changes; the snapshot is rebuilt only then), so reading is a plain read.
             */
            val warehouse: WarehouseStats,
            /**
             * Voices that made no sound: their sample had not arrived (or was empty) or their sound was unknown, summed
             * over the playbacks (KLANG-00399). Counted apart from the late voices, which the warehouse stats carry.
             */
            val soundlessVoices: Int = 0,
        ) : Feedback {
            data class CylinderState(
                /** Cylinder ID (0-15 typically) */
                val id: Int,
                /** Whether this cylinder is currently active (processing audio or effect tails) */
                val active: Boolean,
            )

            /**
             * One line per part of the warehouse. Counters are monotone since backend start;
             * `idle*` are current. Bytes are Double (no Long on the wire or in audio paths).
             */
            data class WarehouseStats(
                /** Delay rings: idle bytes on the shelf, idle count, dirty (not yet zeroed) count. */
                val ringIdleBytes: Double,
                val ringIdleCount: Int,
                val ringDirtyCount: Int,
                val ringAllocations: Int,
                val ringHits: Int,
                val ringFailures: Int,
                val ringDropped: Int,
                val ringSyncCleans: Int,
                /** Reverb networks: one size each (~200 KB at 44.1 kHz). */
                val reverbIdleCount: Int,
                val reverbDirtyCount: Int,
                val reverbAllocations: Int,
                val reverbHits: Int,
                val reverbFailures: Int,
                val reverbDropped: Int,
                /** Whole cylinders. */
                val cylinderIdleCount: Int,
                val cylinderAllocations: Int,
                val cylinderHits: Int,
                val cylinderDropped: Int,
                /** Shared scratch: pre-sized capacity and the deepest nesting ever served. */
                val scratchCapacity: Int,
                val scratchHighWater: Int,
                val scratchLateAllocations: Int,
                val scratchUnbalancedReleases: Int,
                /** Sample PCM resident in the store, and uploads whose PCM could not be allocated. */
                val sampleBytes: Double,
                val sampleCount: Int,
                val sampleAllocationFailures: Int,
                /** Across all live engines: voices dropped at admission (late), and refused unit rents. */
                val droppedVoices: Int,
                val deniedRents: Int,
            ) {
                companion object {
                    /** A warehouse that holds nothing and has done nothing — for specs and placeholders. */
                    val empty = WarehouseStats(
                        ringIdleBytes = 0.0, ringIdleCount = 0, ringDirtyCount = 0, ringAllocations = 0, ringHits = 0,
                        ringFailures = 0, ringDropped = 0, ringSyncCleans = 0,
                        reverbIdleCount = 0, reverbDirtyCount = 0, reverbAllocations = 0, reverbHits = 0,
                        reverbFailures = 0, reverbDropped = 0,
                        cylinderIdleCount = 0, cylinderAllocations = 0, cylinderHits = 0, cylinderDropped = 0,
                        scratchCapacity = 0, scratchHighWater = 0, scratchLateAllocations = 0, scratchUnbalancedReleases = 0,
                        sampleBytes = 0.0, sampleCount = 0, sampleAllocationFailures = 0,
                        droppedVoices = 0, deniedRents = 0,
                    )
                }
            }
        }
    }

    /** Frontend to backend buffer */
    private val controlBuffer = KlangRingBuffer<Cmd>(capacity)

    /** Backend to frontend buffer */
    private val feedbackBuffer = KlangRingBuffer<Feedback>(capacity)

    /** The interface for the Audio Frontend (UI/Main Thread). */
    val frontend = FrontendEndpoint()

    /** The interface for the Audio Backend (DSP/Audio Thread). */
    val backend = BackendEndpoint()

    /**
     * Clears the buffers.
     */
    fun clear() {
        controlBuffer.clear()
        feedbackBuffer.clear()
    }

    /** Endpoint for the frontend. */
    inner class FrontendEndpoint {
        val control: KlangMessageSender<Cmd> get() = controlBuffer
        val feedback: KlangMessageReceiver<Feedback> get() = feedbackBuffer
    }

    /** Endpoint for the backend. */
    inner class BackendEndpoint {
        val control: KlangMessageReceiver<Cmd> get() = controlBuffer
        val feedback: KlangMessageSender<Feedback> get() = feedbackBuffer
    }
}
